import { ReactNode } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { Chip, Link, Stack } from '@mui/material';
import get from 'lodash/get';
import _ from 'lodash';
import startCase from 'lodash/startCase';
import { hasValue } from '@/pages/utils';
import { FCRAWorker, FCRAWorkerAdverse } from '@/types/turn-api';
import { FlagChip } from './styled';
import type { FieldVM, TableFieldVM } from './types';

export const boolLabel = (v: unknown) => {
  if (v === true || v === 'True') return 'Yes';
  if (v === false || v === 'False') return 'No';
  return v == null ? '' : String(v);
};

const digitsOnly = (s?: string) => (s ?? '').replace(/\D/g, '');

/** Render field only if it has value */
export const fieldIf = (
  record: unknown,
  source: string,
  field: FieldVM | TableFieldVM
): FieldVM | TableFieldVM | null => {
  if (!hasValue(record, source)) return null;
  return { ...field };
};

export const formatPhone = (raw?: string) => {
  const digits = digitsOnly(raw);
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw ?? '';
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

export const formatDate = (date?: string) => {
  const trimmed = date?.trim() ?? '';
  if (!trimmed) return '';
  const d = parseISO(trimmed);
  return isValid(d) ? format(d, 'MM-dd-yyyy') : trimmed;
};

export const getFlagsChips = (record: unknown, source: string): ReactNode => {
  const flags = get(record, source) || {};
  const keys = Object.keys(flags);

  if (!keys.length) return null;

  return (
    <Stack key={`${source}-flag`} direction="row" flexWrap="wrap" mt={1.7} spacing={0.7} useFlexGap>
      {keys.map((key) => (
        <FlagChip key={key} size="small" variant="outlined" label={key} />
      ))}
    </Stack>
  );
};

export const getOneOffCheckChips = (record: unknown, source: string): ReactNode => {
  const checks = (get(record, source) ?? []) as Array<{ id?: number; name?: string }>;

  if (!checks.length) return null;

  return (
    <Stack key={`${source}-one-off-check`} direction="row" flexWrap="wrap" mt={1.7} spacing={0.7} useFlexGap>
      {checks.map((check) => (
        <FlagChip
          key={`${source}-${check.id ?? check.name}`}
          size="small"
          variant="outlined"
          label={check.name ?? ''}
        />
      ))}
    </Stack>
  );
};

export const getAdverseAttachmentFields = (record: FCRAWorkerAdverse.Details, source: string): ReactNode => {
  const attachments = get(record, source) || [];

  if (!attachments.length) return null;

  return (
    <Stack key={`${source}-attachment`} direction="column" spacing={0.7}>
      {attachments.map((attachment: FCRAWorkerAdverse.AdverseAttachment) => {
        const sanitizedName = attachment.name?.replace(/\.[^/.]+$/, '') ?? '';

        return (
          <Link key={attachment.url ?? attachment.name} href={attachment.url} underline="hover">
            {sanitizedName}
          </Link>
        );
      })}
    </Stack>
  );
};

export const getMatchChip = (value: FCRAWorker.MatchType, field: string) => {
  if (value === 'none') return null;

  if (value === 'not_matched')
    return <Chip size="small" color="warning" variant="outlined" label={`${field} mismatch`} />;

  return <Chip size="small" color="success" variant="outlined" label={`${field} match`} />;
};

export const pendingChecksLabel = (record: unknown, source: string) => {
  const arr = get(record, source) as unknown;
  if (!Array.isArray(arr)) return '';
  return arr.map((item) => _.startCase(_.toLower(String(item)))).join(', ');
};

export const text = (record: unknown, source: string) => {
  const v = get(record, source);
  return v == null ? '' : String(v);
};

/** Display-only formatting for header Applicant Source chip; leaves numeric and mixed IDs unchanged. */
export const formatApplicantSourceBadgeDisplay = (raw: string): string => {
  const v = raw.trim();
  if (!v) return v;
  if (/^[a-z]+(?:_[a-z]+)*$/.test(v)) {
    return startCase(v);
  }
  return v;
};
